/**
 * i18n entry: default PT, with EN + ES. Strings are split by domain under
 * `locales/<lang>/*.json` and merged in `buildResources.js`. See README.md.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { enTranslation, esTranslation, ptTranslation } from './buildResources.js';

const resources = {
  pt: {
    translation: ptTranslation
  },
  en: {
    translation: enTranslation
  },
  es: {
    translation: esTranslation
  }
};

const getNestedValue = (source, key) => (
  String(key || '')
    .split('.')
    .reduce((current, part) => (current && current[part] !== undefined ? current[part] : undefined), source)
);

const resolveLanguageCandidates = (lng) => {
  const requested = Array.isArray(lng) ? lng : [lng];

  return [...new Set(
    requested
      .flatMap((language) => {
        if (!language) {
          return [];
        }

        const normalized = String(language);
        const baseLanguage = normalized.split('-')[0];
        return [normalized, baseLanguage];
      })
      .filter(Boolean)
  )];
};

const keyExistsInLoadedResources = (lng, ns, key) => {
  const namespace = ns || 'translation';
  const languages = resolveLanguageCandidates(lng);

  return languages.some((language) => {
    const bundle = resources[language]?.[namespace];
    return getNestedValue(bundle, key) !== undefined;
  });
};

i18n
  .use(initReactI18next)
  .init({
    resources,

    lng: 'pt',
    fallbackLng: {
      es: ['en', 'pt'],
      pt: ['en'],
      en: ['pt'],
      default: ['en', 'pt']
    },

    supportedLngs: ['pt', 'en', 'es'],

    // Namespace support
    ns: ['translation'],
    defaultNS: 'translation',

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
      format: function(value, format) {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        if (format === 'currency') {
          // Format currency for Brazilian Real
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(value);
        }
        return value;
      }
    },

    // React-specific options
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em', 'b', 'u']
    },

    // Development options
    debug: process.env.NODE_ENV === 'development',

    // Save missing translations in development
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'development' && !keyExistsInLoadedResources(lng, ns, key)) {
        console.warn(`Missing translation key: ${lng}.${ns}.${key}`);
      }
    },

    // Parse missing key handler
    parseMissingKeyHandler: (key) => {
      return process.env.NODE_ENV === 'development' ? `[Missing: ${key}]` : key;
    }
  });

export default i18n;
